import { Box, Grid, Skeleton, Stack } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { DataGridSkeleton } from "hooks/omnisearch-v2/DataGridSkeleton";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";

import { SectionCard } from "../../../Manifests/sections/SectionCard";

/**
 * Placeholders shaped like the stream details' sections, in the same cards and
 * grid. The sections that load on their own after the details arrive (profile,
 * destinations, line items) use the same placeholder as their Suspense
 * fallback, so nothing changes shape when the panel resolves.
 */

function SectionHeaderSkeleton({ width = 160, action = false }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 1.5, minHeight: 32 }}
    >
      <Skeleton variant="text" width={width} sx={{ typography: "overline" }} />
      {action && <Skeleton variant="rounded" width={120} height={31} />}
    </Stack>
  );
}

/** A label over its value, at the sizes `DisplayOnlyField` draws them. */
function FieldSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" width="45%" sx={{ fontSize: "0.75rem" }} />
      <Skeleton variant="text" width="75%" sx={{ fontSize: "1rem" }} />
    </Box>
  );
}

function FieldGridSkeleton({ count, size }: { count: number; size: Record<string, number> }) {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: count }, (_, i) => (
        <Grid key={i} size={size}>
          <FieldSkeleton />
        </Grid>
      ))}
    </Grid>
  );
}

function HeaderSkeleton({ showCloseButton }: { showCloseButton: boolean }) {
  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Skeleton variant="text" width="40%" sx={{ typography: "h5" }} />
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pl: 5 }}>
          <Stack alignItems="flex-end">
            <Skeleton variant="text" width={150} sx={{ typography: "caption" }} />
            <Skeleton variant="text" width={130} sx={{ typography: "caption" }} />
          </Stack>
          {showCloseButton && <Skeleton variant="circular" width={30} height={30} />}
        </Stack>
      </Stack>
      <Skeleton variant="text" width="55%" sx={{ typography: "caption" }} />
    </Box>
  );
}

/** Twelve two-up fields, then DOT description, tags and the note box. */
function DetailsCardSkeleton() {
  return (
    <SectionCard>
      <SectionHeaderSkeleton width={70} />
      <Grid container spacing={2}>
        {Array.from({ length: 12 }, (_, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6 }}>
            <FieldSkeleton />
          </Grid>
        ))}
        <Grid size={12}>
          <FieldSkeleton />
        </Grid>
        <Grid size={12}>
          <FieldSkeleton />
        </Grid>
        <Grid size={12}>
          <Skeleton variant="rounded" height={95} />
        </Grid>
      </Grid>
    </SectionCard>
  );
}

/** Four summary figures over the chart, which grows to the Details card's height. */
function ShipmentsCardSkeleton() {
  return (
    <SectionCard>
      <Stack sx={{ height: "100%" }}>
        <SectionHeaderSkeleton width={220} />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            columnGap: 4,
            rowGap: 2,
            mb: 2,
          }}
        >
          {Array.from({ length: 4 }, (_, i) => (
            <Box key={i}>
              <Skeleton variant="text" width={70} sx={{ typography: "caption" }} />
              <Skeleton variant="text" width={100} sx={{ typography: "body2" }} />
            </Box>
          ))}
        </Box>
        <Skeleton variant="rounded" sx={{ flex: 1, minHeight: 200, height: "auto" }} />
      </Stack>
    </SectionCard>
  );
}

function DiscrepanciesCardSkeleton() {
  return (
    <SectionCard>
      <SectionHeaderSkeleton width={120} action />
      <Skeleton variant="text" width="60%" sx={{ typography: "body2" }} />
    </SectionCard>
  );
}

/** Characterization, profile & document, constituents and testing schedule. */
export function WasteProfileSectionsSkeleton() {
  return (
    <Stack gap={3}>
      <SectionCard>
        <SectionHeaderSkeleton width={140} action />
        <Stack spacing={2}>
          <FieldGridSkeleton count={10} size={{ xs: 12, sm: 6, lg: 3 }} />
          <FieldGridSkeleton count={4} size={{ xs: 12, lg: 6 }} />
        </Stack>
      </SectionCard>
      <SectionCard>
        <SectionHeaderSkeleton width={150} action />
        <Stack spacing={2}>
          <FieldGridSkeleton count={3} size={{ xs: 12, sm: 6, lg: 4 }} />
          <Skeleton variant="rounded" height={50} />
          <Skeleton variant="rounded" height={96} />
        </Stack>
      </SectionCard>
      <SectionCard>
        <SectionHeaderSkeleton width={110} action />
        <Skeleton variant="text" width="35%" sx={{ typography: "body2" }} />
      </SectionCard>
      <SectionCard>
        <SectionHeaderSkeleton width={140} action />
        <Stack spacing={2}>
          <Skeleton variant="text" width="45%" sx={{ typography: "body2" }} />
          <Skeleton variant="text" width="30%" sx={{ typography: "body2" }} />
        </Stack>
      </SectionCard>
    </Stack>
  );
}

/** One destination card with a management method, then the methods roll-up. */
export function DestinationCardsSkeleton() {
  return (
    <Stack spacing={2.5}>
      <Box>
        <Skeleton variant="text" width={110} sx={{ typography: "overline" }} />
        <Skeleton variant="rounded" height={176} sx={{ mt: 1 }} />
      </Box>
      <Box>
        <Skeleton variant="text" width={160} sx={{ typography: "overline" }} />
        <Skeleton variant="rounded" height={86} sx={{ mt: 1 }} />
      </Box>
    </Stack>
  );
}

/** The line-items grid opens on five rows. */
export function ManifestLineItemsGridSkeleton() {
  return <DataGridSkeleton rows={5} />;
}

function ManifestLineItemsCardSkeleton() {
  return (
    <SectionCard>
      <Stack spacing={2}>
        <SectionHeaderSkeleton width={170} />
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={90} height={36} />
          <Skeleton variant="rounded" width={150} height={31} />
        </Stack>
        <ManifestLineItemsGridSkeleton />
      </Stack>
    </SectionCard>
  );
}

/** The whole stream details drawer while its stream, facility and shipments load. */
export function WasteStreamDetailsModalSkeleton({
  showCloseButton = true,
}: {
  /** Off when the host draws its own close. */
  showCloseButton?: boolean;
}) {
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  return (
    <>
      <HeaderSkeleton showCloseButton={showCloseButton} />
      <Stack gap={3} px={3} py={2}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 5fr) minmax(0, 7fr)" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <DetailsCardSkeleton />
          <Stack spacing={3} useFlexGap sx={{ minWidth: 0 }}>
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
              <ShipmentsCardSkeleton />
            </Box>
            {isStaff && <DiscrepanciesCardSkeleton />}
          </Stack>
        </Box>
        {featureFlags[FeatureFlagKey.ContainerTracking] && <WasteProfileSectionsSkeleton />}
        <SectionCard>
          <SectionHeaderSkeleton width={280} action />
          <DestinationCardsSkeleton />
        </SectionCard>
        <ManifestLineItemsCardSkeleton />
      </Stack>
    </>
  );
}
